/**
 * 侧边密码强度指示器组件
 * Vercel 风格 - 简洁、干净、黑白灰为主
 */

import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { usePasswordStrength } from "@/hooks/usePasswordStrength";
import { Check } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export interface PasswordStrengthSideIndicatorProps {
  /** 密码值 */
  password: string;
  /** 是否显示（通常绑定到 focus 状态） */
  visible: boolean;
  /** 自定义类名 */
  className?: string;
}

/**
 * 侧边密码强度指示器 - Vercel 风格
 */
export default function PasswordStrengthSideIndicator({
  password,
  visible,
  className,
}: PasswordStrengthSideIndicatorProps) {
  const { t } = useTranslation("login");
  const { criteria } = usePasswordStrength(password);

  // 计算满足的条件数量
  const metCount = Object.values(criteria).filter(Boolean).length;

  // Vercel 风格：简洁的强度文本
  const getStrengthText = () => {
    if (!password) return t("passwordStrength.enterPassword", "输入密码");
    if (metCount <= 1) return t("passwordStrength.weak", "弱");
    if (metCount <= 3) return t("passwordStrength.medium", "中");
    return t("passwordStrength.strong", "强");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -8 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className={cn(
            "absolute left-full top-0 ml-3 w-52",
            "rounded-md border border-border bg-popover p-3 shadow-md",
            "dark:bg-zinc-900 dark:border-zinc-800",
            className
          )}
        >
          {/* 箭头 */}
          <div className="absolute -left-[5px] top-3 h-2.5 w-2.5 rotate-45 border-l border-b border-border bg-popover dark:bg-zinc-900 dark:border-zinc-800" />

          {/* 强度条 - Vercel 风格分段式 */}
          <div className="flex gap-1 mb-3">
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={i}
                className={cn(
                  "h-1 flex-1 rounded-full",
                  "bg-zinc-200 dark:bg-zinc-700"
                )}
              >
                <motion.div
                  className={cn(
                    "h-full rounded-full",
                    metCount > i
                      ? metCount <= 1
                        ? "bg-red-500"
                        : metCount <= 3
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                      : "bg-transparent"
                  )}
                  initial={{ width: 0 }}
                  animate={{ width: metCount > i ? "100%" : "0%" }}
                  transition={{ duration: 0.2, delay: i * 0.05 }}
                />
              </motion.div>
            ))}
          </div>

          {/* 强度文本 */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-muted-foreground">
              {t("passwordStrength.label", "密码强度")}
            </span>
            <span
              className={cn(
                "text-xs font-medium",
                !password
                  ? "text-muted-foreground"
                  : metCount <= 1
                    ? "text-red-500"
                    : metCount <= 3
                      ? "text-amber-500"
                      : "text-emerald-500"
              )}
            >
              {getStrengthText()}
            </span>
          </div>

          {/* 分隔线 */}
          <div className="h-px bg-border dark:bg-zinc-800 mb-3" />

          {/* 密码要求列表 - Vercel 风格 */}
          <ul className="space-y-2">
            <CriteriaItem
              met={criteria.minLength}
              label={t("passwordStrength.minLength", "至少 6 个字符")}
            />
            <CriteriaItem
              met={criteria.hasUppercase}
              label={t("passwordStrength.hasUppercase", "大写字母")}
            />
            <CriteriaItem
              met={criteria.hasLowercase}
              label={t("passwordStrength.hasLowercase", "小写字母")}
            />
            <CriteriaItem
              met={criteria.hasNumber}
              label={t("passwordStrength.hasNumber", "数字")}
            />
            <CriteriaItem
              met={criteria.hasSpecialChar}
              label={t("passwordStrength.hasSpecialChar", "特殊字符")}
            />
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * 标准项组件 - Vercel 风格
 */
function CriteriaItem({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <div
        className={cn(
          "flex items-center justify-center h-4 w-4 rounded-full transition-colors duration-150",
          met ? "bg-emerald-500" : "border border-zinc-300 dark:border-zinc-600"
        )}
      >
        {met && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
      </div>
      <span
        className={cn(
          "text-xs transition-colors duration-150",
          met ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {label}
      </span>
    </li>
  );
}
